import React from "react";
import { useSearchParams } from "react-router-dom";
import "./Gantt.css";

// Icon-only switch between a page's views (cards, list, details, Gantt).

const iconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

export const VIEW_MODES = {
  card: {
    label: "Card view",
    icon: (
      <svg {...iconProps}>
        <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
        <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
        <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
        <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
      </svg>
    ),
  },
  list: {
    label: "List view",
    icon: (
      <svg {...iconProps}>
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <circle cx="4" cy="6" r="1" fill="currentColor" />
        <circle cx="4" cy="12" r="1" fill="currentColor" />
        <circle cx="4" cy="18" r="1" fill="currentColor" />
      </svg>
    ),
  },
  details: {
    label: "Details view",
    icon: (
      <svg {...iconProps}>
        <rect x="3" y="3" width="7" height="9" rx="1.5" />
        <rect x="14" y="3" width="7" height="5" rx="1.5" />
        <rect x="14" y="12" width="7" height="9" rx="1.5" />
        <rect x="3" y="16" width="7" height="5" rx="1.5" />
      </svg>
    ),
  },
  gantt: {
    label: "Gantt chart view",
    icon: (
      <svg {...iconProps}>
        <line x1="3" y1="3" x2="3" y2="21" />
        <rect x="6" y="5" width="8" height="3.5" rx="1" />
        <rect x="10" y="10.25" width="9" height="3.5" rx="1" />
        <rect x="8" y="15.5" width="6" height="3.5" rx="1" />
      </svg>
    ),
  },
};

// The chosen view lives in the URL (?view=...) so it can be shared and
// survives a refresh. The default mode is left out of the URL.
export function useViewModeParam(modes, defaultMode = modes[0]) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("view");
  const viewMode = modes.includes(requested) ? requested : defaultMode;
  const setViewMode = (mode) => {
    const next = new URLSearchParams(searchParams);
    if (mode === defaultMode) next.delete("view");
    else next.set("view", mode);
    setSearchParams(next, { replace: true });
  };
  return [viewMode, setViewMode];
}

export default function ViewModeSwitch({
  modes,
  value,
  onChange,
  labels = {},
  ariaLabel = "View",
}) {
  return (
    <div className="view-mode-switch" role="radiogroup" aria-label={ariaLabel}>
      {modes.map((mode) => {
        const label = labels[mode] || VIEW_MODES[mode].label;
        return (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={value === mode}
            aria-label={label}
            title={label}
            className={value === mode ? "active" : ""}
            onClick={() => onChange(mode)}
          >
            {VIEW_MODES[mode].icon}
          </button>
        );
      })}
    </div>
  );
}
